import format from 'date-fns/format';
import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import DownloadIcon from '@material-ui/icons/GetApp';
import ViewIcon from '@material-ui/icons/Visibility';
import AlertIcon from '@material-ui/icons/ErrorOutline';
import Grid from '@material-ui/core/Grid';
import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';
import Tag from 'components/CncUI/Tag';
import FileViewer from 'components/FileViewer';
import FileDownloader from 'components/FileDownloader';

import useQuery from 'hooks/useQuery';
import base64ToArrayBuffer from 'utilities/base64ToArrayBuffer';
import parseAddress from '../utils/parseAddress';

const useStyles = makeStyles(({ palette }) => ({
  green: {
    color: palette.cncUI.default.primary,
  },
}));

const Row = ({ label, value }) => {
  const classes = useStyles();
  return (
    <Grid item xs={12}>
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <Typography
            variant="body1"
            size="short"
            align="right"
            className={classes.green}
            noMargin
          >
            {label}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography variant="body2" size="short" noMargin>
            {value}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

const InfoPanel = ({ loanout = null, offer = false }) => {
  const classes = useStyles();
  const [downloader, setDownloader] = useState('');
  const [viewer, setViewer] = useState('');
  const getLoanoutUrl = useQuery('getLoanoutUrl', {
    scopes: 'payroll_employee:edit',
    request: id => ({ id }),
    response: res => res.data.getLoanoutUrl || '',
  });
  const getLoanoutFile = useQuery('getLoanoutFile', {
    scopes: 'payroll_employee:edit',
    request: id => ({ id }),
    response: res => {
      const data = res.data.getLoanoutFile || '';
      if (data) {
        const arraybuffer = base64ToArrayBuffer(data);
        const fileBlob = new Blob([arraybuffer], { type: 'application/pdf' });
        return URL.createObjectURL(fileBlob);
      }
      return '';
    },
  });

  const loanoutOff = [
    <Grid item xs={12} key="title">
      <Typography variant="h4" noMargin>
        You have no loan-out registered
      </Typography>
    </Grid>,
    <Grid item xs={12} key="content">
      <Typography variant="body2" size="short" noMargin>
        Job offers and jobs paid through Cast & Crew will be made directly to
        you.
      </Typography>
    </Grid>,
    !offer ? (
      <Grid item xs={12} key="disclaimer">
        <Grid container spacing={1}>
          <Grid item style={{ width: 32 }}>
            <AlertIcon className={classes.green} />
          </Grid>
          <Grid item style={{ flex: 1 }}>
            <Typography variant="body2" medium size="short" noMargin>
              Adding a loan-out to your profile does not necessitate that you
              will be paid as a loan-out on a given project. Please work with
              your production.
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    ) : null,
  ];

  return (
    <Grid container spacing={2}>
      {!loanout ? (
        loanoutOff
      ) : (
        <>
          <Grid item xs={12}>
            <Typography variant="h4" noMargin className={classes.green}>
              Loanout is ON
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" size="short" noMargin>
              The loanout shown below can be used to accept job offers and
              payments in Start+.
            </Typography>
          </Grid>
          {!offer && (
            <Grid item xs={12} key="disclaimer">
              <Grid container spacing={1}>
                <Grid item style={{ width: 32 }}>
                  <AlertIcon className={classes.green} />
                </Grid>
                <Grid item style={{ flex: 1 }}>
                  <Typography variant="body2" medium size="short" noMargin>
                    Adding a loan-out to your profile does not necessitate that
                    you will be paid as a loan-out on a given project. Please
                    work with your production.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} style={{ marginTop: 16 }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Row
                    label="Tax-ID or FEIN"
                    value={`***-**-${loanout.taxId.slice(-4)}`}
                  />
                  <Row label="Name" value={loanout.name} />
                  <Row label="Type" value={loanout.type} />
                  <Row label="Address" value={parseAddress(loanout)} />
                  <Grid item xs={12}>
                    <Grid container spacing={4}>
                      <Grid item xs={4}>
                        <Typography
                          variant="body1"
                          size="short"
                          align="right"
                          className={classes.green}
                          noMargin
                        >
                          W-9
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Grid container spacing={1}>
                          <Grid item>
                            <Button
                              secondary
                              short
                              disabled={!loanout.w9}
                              onClick={() => setViewer(loanout.w9)}
                            >
                              <ViewIcon /> View Document
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              secondary
                              short
                              disabled={!loanout.w9}
                              onClick={() => setDownloader(loanout.w9)}
                            >
                              <DownloadIcon /> Download Document
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={4}>
                      <Grid item xs={4}>
                        <Typography
                          variant="body1"
                          size="short"
                          align="right"
                          className={classes.green}
                          noMargin
                        >
                          Indemnification Agreement
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Grid container spacing={1}>
                          <Grid item>
                            <Button
                              secondary
                              short
                              disabled={!loanout.loi}
                              onClick={() => setViewer(loanout.loi)}
                            >
                              <ViewIcon /> View Document
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              secondary
                              short
                              disabled={!loanout.loi}
                              onClick={() => setDownloader(loanout.loi)}
                            >
                              <DownloadIcon /> Download Document
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={4}>
                      <Grid item xs={4}>
                        <Typography
                          variant="body1"
                          size="short"
                          align="right"
                          className={classes.green}
                          noMargin
                        >
                          Loan-out Last Updated
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" size="short" noMargin>
                          {format(
                            new Date(parseInt(loanout.createdAt, 10)),
                            'PPpp',
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={4}>
                      <Grid item xs={4}>
                        <Typography
                          variant="body1"
                          size="short"
                          align="right"
                          className={classes.green}
                          noMargin
                        >
                          Status
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        {loanout.isValid ? (
                          <Tag color="green">Valid</Tag>
                        ) : (
                          <Tag color="red">Expired</Tag>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {viewer && (
            <FileViewer
              fileFetcher={getLoanoutFile}
              config={viewer}
              onClose={() => setViewer('')}
            />
          )}
          {downloader && (
            <FileDownloader
              urlFetcher={getLoanoutUrl}
              config={downloader}
              onClose={() => setDownloader('')}
            />
          )}
        </>
      )}
    </Grid>
  );
};

export default InfoPanel;
